/**
 * Adventure Store
 *
 * Manages state for the Adventure Mode (co-op PvE) system using Zustand.
 * Syncs with the backend API at /api/adventures/* and /api/adventure-worlds/*.
 */

import { create } from 'zustand';
import { getApiUrl } from '../config/api';
import { getAuthTokenWithRetry as getAuthToken } from '../services/auth-token';

// =====================================================================
// API UTILITIES
// =====================================================================

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  requiresAuth: boolean = true
): Promise<T> {
  const url = `${getApiUrl()}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>
  };

  if (requiresAuth) {
    const token = await getAuthToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'API request failed');
  }

  return data;
}

// =====================================================================
// TYPES
// =====================================================================

export interface AdventureWorldSummary {
  worldId: string;
  name: string;
  narrative: string;
  theme: string;
  difficulty: string;
}

export interface InviteCodeLookup {
  adventureId: string;
  worldName: string;
  hostDisplayName: string;
  status: string;
}

export interface AdventureData {
  adventureId: string;
  worldId: string;
  players: {
    host: { userId: string; displayName: string };
    partner: { userId: string; displayName: string } | null;
  };
  status: string;
  currentPosition: { q: number; r: number };
  revealedHexes: string[];
  completedEncounters: Record<string, { stars: number; completedAt: string; attempts: number }>;
  creatureStates: Record<string, { currentHp: number; maxHp: number; status: string; ownerId: string }>;
  usedRestPoints: string[];
  isSolo?: boolean;
  inviteCode?: string;
  totalStars: number;
  encountersDefeated: number;
}

export interface WorldData {
  worldId: string;
  name: string;
  narrative: string;
  theme: string;
  difficulty: string;
  mapDefinition: {
    width: number;
    height: number;
    startPosition: { q: number; r: number };
    hexes: Array<{
      q: number;
      r: number;
      type: string;
      terrain?: string;
      encounterId?: string;
      gatedBy?: string;
      healPercent?: number;
      revive?: boolean;
      flavorText?: string;
    }>;
  };
  encounters: Record<string, {
    name: string;
    narrative: string;
    mode: string;
    difficulty: string;
    previewHint: string;
    battlefieldConfig: {
      width: number;
      height: number;
      orientation?: 'left_vs_right' | 'top_vs_bottom';
      playerDeployCols: number;
      enemyDeployCols: number;
      playerDeployRows?: number;
      enemyDeployRows?: number;
      obstacles?: Array<{ q: number; r: number }>;
    };
    enemyRefs: Array<{ concept: string; count: number; difficultyTier: string }>;
  }>;
  attritionConfig: {
    enabled: boolean;
    restPointHealPercent: number;
    victoryHealPercent: number;
    reviveAtRestPoint: boolean;
  };
}

// =====================================================================
// API RESPONSE TYPES
// =====================================================================

interface WorldsListResponse {
  success: boolean;
  worlds: AdventureWorldSummary[];
}

interface ActiveAdventureResponse {
  success: boolean;
  adventure: AdventureData | null;
}

interface CreateAdventureResponse {
  success: boolean;
  adventure: AdventureData;
}

interface JoinAdventureResponse {
  success: boolean;
  adventure: AdventureData;
}

interface LookupInviteCodeResponse {
  success: boolean;
  result: InviteCodeLookup | null;
}

// =====================================================================
// STORE INTERFACE
// =====================================================================

interface AdventureStore {
  // Session data
  adventure: AdventureData | null;
  world: WorldData | null;
  partnerConnected: boolean;

  // Map state
  revealedHexes: Set<string>;
  currentPosition: { q: number; r: number } | null;

  // Battle state
  currentEncounterId: string | null;
  battlePhase: 'none' | 'preview' | 'deployment' | 'combat' | 'results';
  matchId: string | null;

  // Loading & error
  isLoading: boolean;
  isLoadingWorlds: boolean;
  error: string | null;

  // Available worlds (for hub page)
  availableWorlds: AdventureWorldSummary[];

  // Actions - API calls
  fetchAvailableWorlds: () => Promise<void>;
  fetchActiveAdventure: () => Promise<void>;
  createAdventure: (worldId: string, options?: { isSolo?: boolean }) => Promise<AdventureData>;
  joinAdventure: (inviteCode: string) => Promise<AdventureData>;
  lookupInviteCode: (code: string) => Promise<InviteCodeLookup | null>;

  // State updates (called from socket events)
  setFullState: (adventure: AdventureData, world: WorldData, partnerConnected: boolean) => void;
  updatePosition: (pos: { q: number; r: number }, newReveals: string[]) => void;
  updateCreatureStates: (states: Record<string, { currentHp: number; maxHp: number; status: string; ownerId: string }>) => void;
  setPartnerConnected: (connected: boolean) => void;
  setBattlePhase: (phase: 'none' | 'preview' | 'deployment' | 'combat' | 'results', encounterId?: string, matchId?: string) => void;
  markRestPointUsed: (hexKey: string) => void;
  recordEncounterResult: (encounterId: string, result: { stars: number; completedAt: string; attempts: number }) => void;

  // Reset
  reset: () => void;
}

// =====================================================================
// INITIAL STATE
// =====================================================================

const initialState = {
  // Session data
  adventure: null,
  world: null,
  partnerConnected: false,

  // Map state
  revealedHexes: new Set<string>(),
  currentPosition: null,

  // Battle state
  currentEncounterId: null,
  battlePhase: 'none' as const,
  matchId: null,

  // Loading & error
  isLoading: false,
  isLoadingWorlds: false,
  error: null,

  // Available worlds
  availableWorlds: [],
};

// =====================================================================
// STORE IMPLEMENTATION
// =====================================================================

export const useAdventureStore = create<AdventureStore>((set, get) => ({
  ...initialState,

  // ===================================================================
  // API ACTIONS
  // ===================================================================

  /**
   * Fetch available adventure worlds
   * GET /api/adventure-worlds/list (public)
   */
  fetchAvailableWorlds: async () => {
    set({ isLoadingWorlds: true, error: null });
    try {
      const response = await apiRequest<WorldsListResponse>(
        '/api/adventure-worlds/list',
        { method: 'GET' },
        false // Public endpoint
      );
      set({ availableWorlds: response.worlds, isLoadingWorlds: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch available worlds';
      set({ error: message, isLoadingWorlds: false });
    }
  },

  /**
   * Fetch user's currently active adventure
   * GET /api/adventures/active (auth required)
   */
  fetchActiveAdventure: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiRequest<ActiveAdventureResponse>(
        '/api/adventures/active',
        { method: 'GET' },
        true
      );
      set({ adventure: response.adventure, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch active adventure';
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Create a new adventure session
   * POST /api/adventures/create (auth required)
   */
  createAdventure: async (worldId: string, options?: { isSolo?: boolean }) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiRequest<CreateAdventureResponse>(
        '/api/adventures/create',
        { method: 'POST', body: JSON.stringify({ worldId, isSolo: options?.isSolo }) },
        true
      );
      set({ adventure: response.adventure, isLoading: false });
      return response.adventure;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create adventure';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  /**
   * Join an existing adventure via invite code
   * POST /api/adventures/join (auth required)
   */
  joinAdventure: async (inviteCode: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiRequest<JoinAdventureResponse>(
        '/api/adventures/join',
        { method: 'POST', body: JSON.stringify({ inviteCode }) },
        true
      );
      set({ adventure: response.adventure, isLoading: false });
      return response.adventure;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to join adventure';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  /**
   * Look up an invite code to preview adventure details
   * GET /api/adventures/lookup/:code (auth required)
   */
  lookupInviteCode: async (code: string) => {
    try {
      const response = await apiRequest<LookupInviteCodeResponse>(
        `/api/adventures/lookup/${encodeURIComponent(code)}`,
        { method: 'GET' },
        true
      );
      return response.result;
    } catch {
      return null;
    }
  },

  // ===================================================================
  // SOCKET STATE UPDATES
  // ===================================================================

  /**
   * Set full adventure state (called when receiving adventure:state from socket)
   */
  setFullState: (adventure, world, partnerConnected) => {
    set({
      adventure,
      world,
      partnerConnected,
      currentPosition: adventure.currentPosition,
      revealedHexes: new Set(adventure.revealedHexes),
    });
  },

  /**
   * Update position and reveal new hexes (called on adventure:move-confirmed)
   */
  updatePosition: (pos, newReveals) => {
    const { revealedHexes, adventure } = get();
    const updatedReveals = new Set(revealedHexes);
    for (const key of newReveals) {
      updatedReveals.add(key);
    }
    set({
      currentPosition: pos,
      revealedHexes: updatedReveals,
      adventure: adventure ? {
        ...adventure,
        currentPosition: pos,
        revealedHexes: [...updatedReveals],
      } : null,
    });
  },

  /**
   * Update creature health/status states (called on adventure:battle-result or adventure:rest-applied)
   */
  updateCreatureStates: (states) => {
    const { adventure } = get();
    if (!adventure) return;
    set({
      adventure: { ...adventure, creatureStates: states },
    });
  },

  /**
   * Update partner connection status
   */
  setPartnerConnected: (connected) => {
    set({ partnerConnected: connected });
  },

  /**
   * Set current battle phase and optionally update encounter/match IDs
   */
  setBattlePhase: (phase, encounterId, matchId) => {
    set({
      battlePhase: phase,
      currentEncounterId: encounterId ?? get().currentEncounterId,
      matchId: matchId ?? get().matchId,
    });
  },

  /**
   * Mark a rest point hex as used (called on adventure:rest-applied)
   */
  markRestPointUsed: (hexKey) => {
    const { adventure } = get();
    if (!adventure) return;
    set({
      adventure: {
        ...adventure,
        usedRestPoints: [...adventure.usedRestPoints, hexKey],
      },
    });
  },

  /**
   * Record encounter completion result (called on adventure:battle-result)
   */
  recordEncounterResult: (encounterId, result) => {
    const { adventure } = get();
    if (!adventure) return;

    // Idempotent: check if encounter was already recorded to avoid double-counting
    const existing = adventure.completedEncounters[encounterId];
    const isNewEncounter = !existing;

    // Only increment counters for newly completed encounters
    const encountersDefeated = isNewEncounter
      ? adventure.encountersDefeated + 1
      : adventure.encountersDefeated;
    const starDelta = isNewEncounter
      ? result.stars
      : Math.max(0, result.stars - (existing?.stars ?? 0));
    const totalStars = adventure.totalStars + starDelta;

    set({
      adventure: {
        ...adventure,
        completedEncounters: {
          ...adventure.completedEncounters,
          [encounterId]: result,
        },
        encountersDefeated,
        totalStars,
      },
    });
  },

  // ===================================================================
  // UTILITY ACTIONS
  // ===================================================================

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },
}));
